<template>
	<div class="d-flex flex-column h-100">
		<div class="d-flex h-100 flex-fill justify-content-center align-items-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="2"></b-icon>
		</div>
		<ul v-else-if="channels?.length > 0" class="channel-list">
			<li v-for="channel in channels" :key="`${channel.service_id}-${channel.token}`">
				<div class="channel-rows">
					<div class="content">
						<div class="channel-icon">
							<b-avatar size="3em" :src="channel.info?.avatarUrl||channel.avatar_url" badge-variant=""  badge-offset="-0.4em" :href="channel.status=='enabled'&&channel.info?.url||null" target="_blank">
								<template #badge><b-avatar :src="channel.service_icon_url||null" size="1.3em" class="service_avatar" square :title="channel.service_name"></b-avatar></template>
							</b-avatar>
						</div>
						<div class="info">
							<div class="title-row">
								<div class="channel-name">
									<a :href="channel.status=='enabled'&&channel.info?.url||null" target="_blank">{{channel.title}}</a>
									<span v-b-tooltip.hover.bottom v-if="channel?.verified != 1 && channel.status == 'enabled'" title="not verified (bot cannot send messages)"><b-icon icon="x" variant="danger"></b-icon></span>
								</div>
								<div class="channel-buttons">
									<b-button v-if="!compact" size="sm" variant="default" @click="$emit('edit', channel)" title="Edit channel" v-b-tooltip.hover.left><b-icon icon="pencil-fill" style="width: .8em; height: .8em"></b-icon></b-button>
									<b-form-checkbox switch v-model="channel.status" value="enabled" unchecked-value="disabled" @change="setChannelStatus(channel.service_id, channel.token, channel.status, $event)" :disabled="channel.changingStatus" style="margin-right: -0.25rem" :id="`channel-status-${channel.service_id}-${channel.token}`"></b-form-checkbox>
								</div>
							</div>
							<div class="status-row">
								<div class="service-name">{{channel.service_name}}</div>
								<div class="channel-status">
									<b-badge pill :variant="channel.error ? 'danger' : (channel.info?.numLive>0?'success':'secondary')" v-if="channel.status == 'enabled'" :title="channel.info?.numLive>0?'live':'offline'" v-b-tooltip.hover.left class="mr-1 position-relative"><b-spinner v-show="channel.loading > 0" label="Loading" class="mr-2 spinner-border-xs spinner-absolute"></b-spinner> {{channel.info?.numLive>0 ? `${channel.info?.numViewers} viewers` : 'offline'}}</b-badge>
								</div>
							</div>
						</div>
					</div>
					<b-alert variant="danger" :show="channel.channel_status?.authOk === false" class="m-1">Please reauthenticate this channel</b-alert>
				</div>
			</li>
		</ul>
		<div v-else class="text-center text-muted p-5 flex-fill align-content-end">
			No channels added.<br />
			Please add a channel using the button below.<br />
			<br />
			<br />
			<b-icon icon="arrow-down" font-scale="7.5" animation="cylon-vertical"></b-icon>
		</div>
		<b-popover ref="upgradePopover" :target="upgradePopoverTarget" triggers="manual" placement="right">
			<div class="d-flex justify-content-between align-items-start">
				<h5>Too many channels</h5>

				<b-button
					size="sm"
					variant="link"
					class="p-0 ml-2 text-secondary"
					@click="$refs.upgradePopover.$emit('close')">

					<b-icon icon="x"></b-icon>
				</b-button>
			</div>
			<p>Upgrade your plan to activate more channels.</p>
			<b-button @click="$refs.upgradePopover.$emit('close'); $root.$emit('show-upgrade-modal')">Upgrade now</b-button>
		</b-popover>
	</div>
</template>

<script>
import FetchPoller from '../FetchPoller';

export default {
	data() { return {
		loading: 0,
		channels: [],
		user: null,
		showUpgradePopover: false,
		upgradePopoverTarget: '',
	}},
	props: {
		compact: Boolean,
	},
	async mounted() {
		try {
			this.loading++;
			this.error = null;
			this.user = await this.$api.get('userData');
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				channelInfoUpdate: info => {
					const channel = this.channels.find(c => c.service_id == info.serviceId && c.token == info.channelToken);
					if(!channel) return;
					for(let field of [ 'numViewers', 'numLive', 'title', 'url', 'avatarUrl', 'about', 'createdAt', 'numFollowers', 'numSubscribers' ]) {
						if(!('info' in channel) || !channel.info) channel.info = {};
						if(field in info) this.$set(channel.info, field, info[field]);
					}
				},
				channelDeleted: () => this.channelPoller.start(),
				channelCreated: () => this.channelPoller.start(),
				channelStatusChanged: msg => {
					const channel = this.channels.find(c => c.service_id == msg.serviceId && c.token == msg.channelToken);
					if(!channel) this.channelPoller.start(); // if we can't find the channel, we reload them all
					else {
						channel.status = msg.status;
						if(msg.status == 'disabled')
							channel.poller.stop();
						else if(msg.status == 'enabled')
							channel.poller.start();
					}
				},
			});

			this.channelPoller = new FetchPoller(async signal => {
				try {
					this.error = null;
					const channels = await this.$api.get('channels', null, signal);
					for(let channel of channels) {
						const existingChannel = this.channels.find(c => c.service_id == channel.service_id && c.token == channel.token);
						if(!existingChannel) {
							const newChannel = {
								...channel,
								info: null,
								error: null,
								loading: 0,
								changingStatus: false,
								poller: new FetchPoller(async signal => {
									console.log('channel poller', channel.service_id, channel.token);
									newChannel.loading++;
									newChannel.error = null;
									try {
										newChannel.info = await this.$api.get(`channel/${encodeURIComponent(channel.service_id)}/${encodeURIComponent(channel.token)}/info`, {}, signal);
									} catch(e) {
										newChannel.error = e;
									} finally {
										newChannel.loading--;
									}
								}, 60 * 1000),
							};
							this.channels.push(newChannel);
							if(channel.status == 'enabled')
								await newChannel.poller.start();
						} else if(existingChannel.status == 'enabled' && channel.status == 'disabled') {
							await existingChannel.poller.stop();
							for(let k in channel)
								existingChannel[k] = channel[k];
						} else if(existingChannel.status == 'disabled' && channel.status == 'enabled') {
							for(let k in channel)
								existingChannel[k] = channel[k];
							await existingChannel.poller.start();
						} else {
							for(let k in channel)
								existingChannel[k] = channel[k];
						}
					}
					for(let i = 0; i < this.channels.length; i++) {
						const existingChannel = channels.find(channel => this.channels[i].service_id == channel.service_id && this.channels[i].token == channel.token);
						if(!existingChannel) {
							await this.channels[i].poller.stop();
							this.channels.splice(i, 1);
							i--;
						} else if(existingChannel.status == 'disabled' && this.channels[i].status == 'enabled') {
							await this.channels[i].poller.stop();
							for(let k in existingChannel)
								this.channels[i][k] = existingChannel[k];
						} else if(existingChannel.status == 'enabled' && this.channels[i].status == 'disabled') {
							for(let k in existingChannel)
								this.channels[i][k] = existingChannel[k];
							await this.channels[i].poller.start();
						} else {
							for(let k in existingChannel)
								this.channels[i][k] = existingChannel[k];
						}
					}
				} catch(e) {
					if(!(e instanceof DOMException) || !(e.code == 20 || e.name == 'AbortError')) {
						this.error = e;
						console.error('Polling channels', e, e instanceof DOMException, e.code, e.name);
					}
				}
			}, 10 * 60 * 1000);
			await this.channelPoller.start(true);
			if(this.channels.length <= 0)
				this.$parent.$refs.channelModal?.show?.();
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.subId && this.$socket.unsubscribe(this.subId);
		this.channelPoller.stop();
		for(let i = 0; i < this.channels.length; i++)
			this.channels[i].poller.stop();
	},
	watch: {
		showUpgradePopover(val) { console.log('changed showUpgradePopover', val); },
	},
	methods: {
		async setChannelStatus(serviceId, channelToken, status) {
			if(status == 'enabled') {
				console.log('closing popover');
				this.$refs.upgradePopover.$emit('close');
			}
			this.upgradePopoverTarget = `channel-status-${serviceId}-${channelToken}`;
			try {
				const ch = this.channels.find(c => c.service_id == serviceId && c.token == channelToken);
				if(!ch) throw new Error('Could not find channel in list');
				ch.changingStatus = true;
				await this.$api.post(`channel/${encodeURIComponent(serviceId)}/${encodeURIComponent(channelToken)}/status`, { status });
			} catch(e) {
				console.error('Could not set channel status', serviceId, channelToken, status, e.message, e);
				if(e.message == 'Not subscribed') {
					const ch = this.channels.find(c => c.service_id == serviceId && c.token == channelToken);
					if(ch) {
						ch.status = 'disabled';
						this.$refs.upgradePopover.$emit('open');
					}
				} else {
					this.$bvToast.toast(e.message||e, { title: 'Could not change channel status', variant: 'danger', solid: true });
				}
			} finally {
				const ch = this.channels.find(c => c.service_id == serviceId && c.token == channelToken);
				if(ch) ch.changingStatus = false;
			}
		},
	},
}
</script>

<style lang="scss">
@import '../plugins/variable.scss';

.spinner-border-xs {
	width: .6rem;
	height: .6rem;
	border-radius: .3rem;
	border-width: .05em;
}

.spinner-absolute {
	position: absolute;
	left: -.8rem;
}

.position-relative {
	position: relative;
}

td > .text-right input[type=checkbox] {
	left: inherit;
	right: 1rem;
}

ul.channel-list {
	list-style: none;
	margin: 0;
	border: 0;
	padding: 0;

	> li {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		list-style-type: none;
		padding: 4px;
		transition: background-color .15s;

		&:nth-child(odd) {
			background-color: $table-accent-bg;
		}

		> .channel-rows {
			display: flex;
			flex: 1 1;
			flex-direction: column;
			gap: 4px;
			min-width: 0;

			> .content {
				display: flex;

				> .channel-icon {
					height: 48px;
					margin-right: 12px;
					position: relative;
					transition: all .3s ease;
					width: 48px;
				}

				> .info {
					.title-row {
						align-items: center;
						display: flex;
						justify-content: space-between;

						.channel-name {
							align-items: center;
							display: flex;
							transition: opacity .3s ease;
							white-space: nowrap;
							text-overflow: ellipsis;
							overflow: hidden;

							* {
								white-space: nowrap;
								text-overflow: ellipsis;
								overflow: hidden;
							}
						}

						.channel-buttons {
							align-items: center;
							display: flex;
							gap: 8px;
							margin-left: 8px;
						}
					}

					.status-row {
						align-items: center;
						display: flex;
						justify-content: space-between;

						.service-name {
							color: $text-muted;
							white-space: nowrap;
							text-overflow: ellipsis;
							overflow: hidden;
							font-size: .8em;
						}
					}

					display: flex;
					flex: 1 1;
					flex-direction: column;
					min-width: 0;
				}
			}
		}
	}
}
</style>
